import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Pets,
  Cake,
  FitnessCenter,
  MedicalServices,
  Close,
} from '@mui/icons-material';
import { OutlinedInput } from '@mui/material';
import apiService from '../services/api';
import { SkeletonCard } from '../components/Skeletons';
import { INK, MUTED, FONT_DISPLAY, stripeSx } from '../theme/kiff';

/**
 * Behaviour chips use the tinted-fill language from the status chips: energy
 * level is the thing a handler plans a walk around, so it reads as a warning
 * tint at high energy, cobalt at medium, green at low.
 */
const ENERGY_CHIP_SX = {
  high: { color: '#5C3B00', borderColor: '#8C5A00', backgroundColor: '#FFE9A8' },
  medium: { color: '#15308F', borderColor: '#1D46D8', backgroundColor: '#DCE6FF' },
  low: { color: '#0A5C33', borderColor: '#0A5C33', backgroundColor: '#C9F0D8' },
};

const PetProfiles = () => {
  const theme = useTheme();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    breed: '',
    age: '',
    weight: '',
    size: '',
    temperament: [],
    special_instructions: '',
    medical_conditions: '',
    energy_level: '',
  });
  const [petOptions, setPetOptions] = useState({
    temperament_choices: {},
    size_choices: {},
    energy_level_choices: {},
  });

  useEffect(() => {
    fetchPets();
    fetchPetOptions();
  }, []);

  const fetchPetOptions = async () => {
    try {
      const options = await apiService.getPetOptions();
      setPetOptions(options);
    } catch (err) {
      console.error('Error fetching pet options:', err);
    }
  };

  const fetchPets = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getPets();
      // Handle paginated response - extract results array
      const petsArray = data?.results || data;
      // Ensure we always set an array
      setPets(Array.isArray(petsArray) ? petsArray : []);
    } catch (err) {
      setError('Failed to load pets. Please try again.');
      console.error('Error fetching pets:', err);
      // Set to empty array on error
      setPets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPet = () => {
    setEditingPet(null);
    setFormData({
      name: '',
      breed: '',
      age: '',
      weight: '',
      size: '',
      temperament: [],
      special_instructions: '',
      medical_conditions: '',
      energy_level: '',
    });
    setOpenDialog(true);
  };

  const handleEditPet = (pet) => {
    setEditingPet(pet);
    setFormData({
      name: pet.name || '',
      breed: pet.breed || '',
      age: pet.age ? pet.age.toString() : '',
      weight: pet.weight ? pet.weight.toString() : '',
      size: pet.size || '',
      temperament: Array.isArray(pet.temperament) ? pet.temperament : (pet.temperament ? pet.temperament.split(',').map(t => t.trim()) : []),
      special_instructions: pet.special_instructions || '',
      medical_conditions: pet.medical_conditions || '',
      energy_level: pet.energy_level || '',
    });
    setOpenDialog(true);
  };

  const handleSavePet = async () => {
    try {
      setSaving(true);
      setError('');

      const petData = {
        ...formData,
        age: parseInt(formData.age) || 0,
        weight: parseFloat(formData.weight) || 0,
      };

      if (editingPet) {
        // Update existing pet
        const updatedPet = await apiService.updatePet(editingPet.id, petData);
        setPets(prev =>
          Array.isArray(prev) ? prev.map(pet =>
            pet.id === editingPet.id ? updatedPet : pet
          ) : [updatedPet]
        );
      } else {
        // Add new pet
        const newPet = await apiService.createPet(petData);
        setPets(prev => Array.isArray(prev) ? [...prev, newPet] : [newPet]);
      }

      setOpenDialog(false);
    } catch (err) {
      setError('Failed to save pet. Please try again.');
      console.error('Error saving pet:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePet = async (petId) => {
    if (window.confirm('Are you sure you want to delete this pet?')) {
      try {
        await apiService.deletePet(petId);
        setPets(prev => Array.isArray(prev) ? prev.filter(pet => pet.id !== petId) : []);
      } catch (err) {
        setError('Failed to delete pet. Please try again.');
        console.error('Error deleting pet:', err);
      }
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleTemperamentChange = (event) => {
    const value = event.target.value;
    setFormData({ ...formData, temperament: typeof value === 'string' ? value.split(',') : value });
  };

  const getPetAvatar = (petName) => {
    // Generate a simple avatar from pet name
    return petName ? petName.charAt(0).toUpperCase() : '?';
  };

  const formatFieldValue = (value, field) => {
    if (!value) return '';
    
    if (field === 'energy_level') {
      const levelMap = {
        'low': 'Low',
        'medium': 'Medium',
        'high': 'High',
      };
      return levelMap[value] || value;
    }
    
    if (field === 'size') {
      const sizeMap = {
        'XS': 'Extra Small',
        'S': 'Small',
        'M': 'Medium',
        'L': 'Large',
        'XL': 'Extra Large',
      };
      return sizeMap[value] || value;
    }
    
    return value;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <Box>
          <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
            My pets
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The family, on file — handlers see what matters before they arrive.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddPet}>
          Add pet
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State — skeletons in the exact card geometry. */}
      {loading ? (
        <Grid container spacing={3}>
          {[0, 1, 2].map((i) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
              <SkeletonCard sx={{ minHeight: 300 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {/* Pets Grid */}
          {Array.isArray(pets) && pets.length > 0 ? (
        <Grid container spacing={3}>
          {pets.map((pet) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={pet.id}>
              <Card sx={{ height: '100%', position: 'relative' }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: theme.palette.primary.main,
                        fontSize: '1.5rem',
                        mr: 2,
                      }}
                    >
                      {getPetAvatar(pet.name)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {pet.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {pet.breed}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => handleEditPet(pet)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeletePet(pet.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Stats */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<Cake />}
                      label={`${pet.age_display || pet.age || 0}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<FitnessCenter />}
                      label={`${pet.weight || 0} kg`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={pet.size_display || formatFieldValue(pet.size, 'size')}
                      size="small"
                      variant="outlined"
                    />
                    {pet.energy_level && (
                      <Chip
                        label={`${formatFieldValue(pet.energy_level, 'energy_level')} energy`}
                        size="small"
                        sx={ENERGY_CHIP_SX[pet.energy_level] || undefined}
                      />
                    )}
                  </Box>

                  {/* Details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Temperament
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {Array.isArray(pet.temperament) ? pet.temperament.join(', ') : pet.temperament}
                    </Typography>

                    {pet.special_instructions && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Special Instructions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {pet.special_instructions}
                        </Typography>
                      </>
                    )}

                    {pet.medical_conditions && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          <MedicalServices sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          Medical Conditions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pet.medical_conditions}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Pet Info */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    pt: 2, 
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {pet.age_display || pet.age || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Age
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {pet.weight || 0} kg
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Weight
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {pet.size_display || formatFieldValue(pet.size, 'size')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Size
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Dashed add-a-pet slot: the next pet is part of the grid, not a
              toolbar action. Extra pets share a walk for a small add-on. */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key="add-pet">
            <Box
              role="button"
              tabIndex={0}
              aria-label="Add a pet"
              onClick={handleAddPet}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleAddPet();
              }}
              sx={{
                height: '100%',
                minHeight: 280,
                border: `2.5px dashed ${MUTED}`,
                borderRadius: '14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 3,
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: INK,
                  color: 'text.primary',
                  backgroundColor: '#FFFFFF',
                },
              }}
            >
              <Add sx={{ fontSize: 40 }} />
              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 800,
                  fontSize: 14,
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              >
                Add a pet
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 240 }}>
                Extra pets join the same walk for a small add-on — not a second
                booking.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Box
            sx={{
              width: 140,
              height: 140,
              mx: 'auto',
              mb: 3,
              border: `2.5px solid ${INK}`,
              borderRadius: '14px',
              ...stripeSx(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Pets sx={{ fontSize: 64, color: INK, opacity: 0.35 }} />
          </Box>
          <Typography variant="h2" component="h2" sx={{ mb: 1.5 }}>
            No pets on file yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 380, mx: 'auto' }}>
            Add your first — it takes a minute, and handlers see temperament,
            meds and the vet's number before they arrive.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddPet}>
            Add your first pet
          </Button>
        </Box>
      )}
        </>
      )}

      {/* Add/Edit Pet Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {editingPet ? 'Edit Pet Profile' : 'Add New Pet'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Pet's Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Breed/Type"
                value={formData.breed}
                onChange={handleInputChange('breed')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Age (years)"
                type="number"
                value={formData.age}
                onChange={handleInputChange('age')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Weight (kg)"
                type="number"
                value={formData.weight}
                onChange={handleInputChange('weight')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth required>
                <InputLabel>Size</InputLabel>
                <Select
                  value={formData.size}
                  onChange={handleInputChange('size')}
                  label="Size"
                >
                  <MenuItem value="XS">Extra Small (under 5 kg)</MenuItem>
                  <MenuItem value="S">Small (5-11 kg)</MenuItem>
                  <MenuItem value="M">Medium (11-27 kg)</MenuItem>
                  <MenuItem value="L">Large (27-41 kg)</MenuItem>
                  <MenuItem value="XL">Extra Large (over 41 kg)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Temperament</InputLabel>
                <Select
                  multiple
                  value={formData.temperament}
                  onChange={handleTemperamentChange}
                  input={<OutlinedInput label="Temperament" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {Object.entries(petOptions.temperament_choices || {}).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Energy Level</InputLabel>
                <Select
                  value={formData.energy_level}
                  onChange={handleInputChange('energy_level')}
                  label="Energy Level"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Special Instructions"
                multiline
                rows={3}
                value={formData.special_instructions}
                onChange={handleInputChange('special_instructions')}
                placeholder="Any special instructions for handlers..."
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Medical Conditions"
                multiline
                rows={3}
                value={formData.medical_conditions}
                onChange={handleInputChange('medical_conditions')}
                placeholder="Any medical conditions, medications, or allergies..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePet}
            disabled={saving || !formData.name || !formData.breed || !formData.age || !formData.weight || !formData.size}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={20} /> : (editingPet ? 'Update Pet' : 'Add Pet')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PetProfiles; 